import React, { Component } from "react";
import { Icon, TextField } from "@material-ui/core";
import styled from "styled-components";
import { _ } from "js/vendor";
import { getStaticUrl } from "js/config";
import { themeColors } from "js/react/sharedStyles";

import { ChooseThemePane } from "../CreatePresentationDialog/ChooseThemePane";
import { UIPane, UIPaneContents, BlueButton } from "js/react/components/UiComponents";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import Spinner from "js/react/components/Spinner";
import { Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import { AiGenerationController } from "js/editor/AIGeneration/AiGenerationController";
import {
    FindImagesToGenerate,
    GenerateImagesForItem,
    GenerateSlideModel
} from "js/editor/AIGeneration/GenerateSlideModel";
import CanvasPreview from "js/editor/CreatePresentationDialog/CanvasPreview";
import { SearchAssets } from "js/core/utilities/searchAssets";
import { app } from "js/namespaces";
import { FeatureType } from "common/features";
import { Notice } from "js/react/components/Notice";
import { openPricingPage } from "js/core/utilities/externalLinks";
import { AIGenerateUI } from "js/editor/AIGeneration/AIComponents";

const examples = [
    "Investor pitch deck for a Series A SaaS company in the B2C FinTech space",
    "Board deck for Q1 revenue, growth, and new customers",
    "What is Google's business model?",
    "How the Grinch stole Christmas",
    "A pitch deck for a mobile app that sells train tickets called 'Ticket to Ride'",
    "Presentation on the workplace transformation and the future of work",
];

const InnerContainer = styled.div`
  //max-width: 900px;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px 20px;
`;
const Item = styled.div`

`;

const Examples = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

const ExampleButton = styled.div`
  cursor: pointer;
  background: white;
  text-align: left;
  font-style: italic;
  font-size: 15px;
  padding: 14px 20px;
  border-radius: 0px;

  :hover {
    background: ${themeColors.selection};
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  font-weight: bold;
  width: 100%;
  background: white;
  padding: 10px 20px;

  img {
    height: 80px;
  }
`;

const Label = styled.div`
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  .MuiIcon-root {
    margin-right: 6px;
    opacity: 0.75;
    color: ${themeColors.ui_blue}
  }
`;
const InnerPane = styled.div`
  padding: 20px 40px;
  background: white;
  border-radius: 4px;
  width: 100%;
  display: flex;
  flex-direction: column;


  h1 {
    text-transform: none !important;
    font-size: 22px !important;
    margin: 0 !important;
  }

  p {
    font-size: 15px;
    opacity: 0.8;
  }
`;

const Warning = styled.div`
  width: 100%;
  padding: 10px 20px;
  color: rgba(0, 0, 0, 0.85);
  background: ${themeColors.lightBlue};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OpenAiLogoContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 20px;
  display: flex;
  align-items: center;
  z-index: 99999;

  > img {
    margin-right: 10px;
  }

  > a {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 150%;
    color: #666666;
    text-decoration: none;

    &:hover {
      color: #11A9E2;
    }
  }
`;

const SlideDescription = styled.div`
  background: white;
  border: solid 1px #ccc;
  padding: 10px 20px;
  margin-bottom: 10px;
  width: 100%;
  pointer-events: auto;
  user-select: text;
`;

const SlideCanvases = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
const CanvasFrame = styled.div`
  position: relative;
  width: 320px;
  height: 180px;
  border: solid 1px #ccc;
  background: white;

  .canvas-frame {
    transform: scale(.25);
  }

  .loader {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    label {
      position: absolute;
      top: 0px;
      padding: 20px;
      font-weight: 600;
    }

    .spinner-frame {

    }
  }
`;

const StatusLabel = styled.label`
  font-size: 16px;
  font-weight: bold;
`;

export const AiWizardStep = {
    PROMPT: "prompt",
    GENERATING_OUTLINE: "generating-outline",
    BUILD_OUTLINE: "build-outline",
    GENERATING_SLIDES: "generating-slides",
    THEME: "theme",
};

class AICreatePresentationWizard extends Component {
    state = {
        step: AiWizardStep.PROMPT,
        prompt: "",
        hasOutline: false
    }

    onSetPrompt = prompt => {
        this.setState({
            step: AiWizardStep.THEME,
            prompt
        });

        this.generateOutline(prompt);
    }

    generateOutline = async prompt => {
        let outline;
        try {
            outline = await AiGenerationController.GeneratePresentationOutline(prompt);
            // outline = {
            //     title: "Google's Business Model",
            //     image: "business",
            //     slides: [{ title: "Google's advertising revenue", type: "chart" }]
            // }
        } catch (err) {
            console.error("Failed to generate outline", err);

            ShowErrorDialog({ title: "Oops, DesignerBot AI wasn't able to generate a valid presentation outline for your prompt.", message: "This can happen unexpectedly sometimes. You can either try again or try to rephrase your prompt." });
            this.setState({
                step: AiWizardStep.PROMPT
            });
            return;
        }

        outline.slides.insert({
            title: outline.title,
            type: "title"
        }, 0);

        for (let slide of outline.slides) {
            slide.type = slide.type.toLowerCase();
        }

        this.setState({
            hasOutline: true,
            outline,
        });

        if (this.state.theme) {
            this.setState({
                step: AiWizardStep.GENERATING_SLIDES
            });
        }
    }

    onSetTheme = async theme => {
        if (this.state.hasOutline) {
            this.setState({
                step: AiWizardStep.GENERATING_SLIDES
            });
        } else {
            this.setState({
                step: AiWizardStep.GENERATING_OUTLINE
            });
        }

        this.setState({
            theme
        });
    }

    onSlidesGenerated = () => {
        let { outline, theme } = this.state;

        const defaultSlides = outline.slides.filter(slide => slide.model).map(slide => {
            let model = slide.model;
            model.templateId = model.template_id;
            model.data = model.states[0];
            delete model.states;
            return model;
        });

        this.props.onCreate({
            type: "ai-template",
            defaultSlides,
            theme,
            name: outline.title
        });
    }

    handleGoToStep = step => {
        this.setState({ step });
    }

    renderStep() {
        const { description, name, step, outline } = this.state;
        const { requireSharedTheme } = this.props;

        switch (step) {
            case AiWizardStep.PROMPT:
                return (
                    <AIGenerateUI
                        cta="PresentationBot"
                        header="Let DesignerBot build your presentation automatically!"
                        placeholder="Describe your presentation. Be specific and detailed."
                        examples={examples}
                        buttonPrompt="Generate Presentation"
                        onStartGeneration={this.onSetPrompt}
                    />
                );
            case AiWizardStep.THEME:
                return (
                    <ChooseThemePane onCreate={this.onSetTheme}
                        requireSharedTheme={requireSharedTheme} />
                );
            case AiWizardStep.GENERATING_SLIDES:
                return (
                    <GenerateSlidesStep data={outline} onComplete={this.onSlidesGenerated} />
                );
            case AiWizardStep.GENERATING_OUTLINE:
                return (
                    <FlexBox fill center middle vertical>
                        <Label>DesignerBot is working on generating your presentation. This can take up to 2 minutes to
                            complete...</Label>
                        <Gap20 />
                        <img src={getStaticUrl("/images/ai-search/presentation-loading.gif")} />

                        <OpenAiLogoContainer>
                            <img src={getStaticUrl("/images/ai-search/dall-e-logo.png")} />
                            <a href="https://openai.com/" target="_blank">Powered by OpenAI</a>
                        </OpenAiLogoContainer>
                    </FlexBox>
                );
        }
    }

    render() {
        return (
            <UIPane style={{ width: "calc(100vw - 260px)" }}>
                <UIPaneContents style={{ padding: 0, background: themeColors.lightGray }}>
                    <FlexBox top fill column>
                        {this.renderStep()}
                    </FlexBox>
                </UIPaneContents>
            </UIPane>
        );
    }
}

class GenerateSlidesStep extends Component {
    async componentDidMount() {
        let { title, image, slides } = this.props.data;

        let imageSearch = await new SearchAssets().searchImages(image);
        let images = imageSearch.results;

        if (images.length == 0) {
            console.error("No images were returned for search: " + image);
            imageSearch = await new SearchAssets().searchImages("clouds");
            images = imageSearch.results;
        }

        let slidePromises = slides.map(slide => this.generateSlide(title, slide, images));
        await Promise.all(slidePromises);

        this.props.onComplete();
    }

    generateSlide = async (title, slide, images) => {
        try {
            let generateWithAi = true;

            switch (slide.type) {
                case "title":
                    generateWithAi = false;
                    break;
            }

            let response = {};
            if (generateWithAi) {
                let promptOptions = {
                    slideType: slide.type,
                    slideTitle: slide.title,
                    presentationTitle: title
                };

                response = await AiGenerationController.GenerateSlideFromOutline(promptOptions);
            }

            let model = await GenerateSlideModel({ ...slide, ...response }, images);

            let imagesToGenerate = FindImagesToGenerate(model);
            if (imagesToGenerate.length) {
                let imagePromises = [];
                let count = imagesToGenerate.length;

                // this.setState({ generating: `Finding images (${count} remaining)...` });
                for (let image of imagesToGenerate) {
                    imagePromises.push(GenerateImagesForItem(image, () => {
                        count--;
                        // this.setState({ generating: `Finding images (${count} remaining)...` });
                    }));
                }
                await Promise.all(imagePromises);
            }

            slide.model = model;
        } catch (err) {
            console.error("Failed to generate slide", err);
        }
        this.forceUpdate();
    }

    render() {
        const { slides } = this.props.data;

        return (
            <InnerContainer>
                <StatusLabel>Generating slides...</StatusLabel>
                <Gap20 />
                <SlideCanvases>
                    {slides.map(slide =>
                        (<CanvasFrame key={slide.title}>
                            {slide.model && <CanvasPreview slideModel={slide.model} />}
                            {!slide.model &&
                                <div className="loader">
                                    <label>{slide.title}</label>
                                    <Spinner />
                                </div>
                            }
                        </CanvasFrame>)
                    )}
                </SlideCanvases>
            </InnerContainer>
        );
    }
}

export default AICreatePresentationWizard;
